<script setup lang="ts">
definePageMeta({
  layout: false,
})

useSetSEO()
useScrollRestoration()

const { gtEvent } = useGoogleTag()
const { locale } = useI18n()
const etcStore = useEtcStore()
const isLoading = ref(false)
const isError = ref(false)

watch(
  () => locale.value,
  (cur) => {
    if (cur) {
      setTimeout(() => {
        fetchNotices(true)
      })
    }
  }
)

onMounted(() => {
  if (etcStore.noticeList.items.length) return
  fetchNotices(true)
})

// 공지사항 목록 페이징 조회
const fetchNotices = async (refresh = false) => {
  try {
    isLoading.value = true
    isError.value = false
    if (!refresh) {
      gtEvent('scrollEvent', {
        eventCategory: '더보기',
        eventAction: '공지사항 목록 더보기',
        eventLabel: 'loadMore',
        eventSlot: '게시판',
        eventI18nAddr: 'loadMore',
        eventComponent: 'RecyclerView',
        pageNum: etcStore.noticeList.payload?.pageNum,
        pageSize: etcStore.noticeList.payload?.pageSize,
        type: etcStore.noticeList.payload?.type,
      })
    }
    await etcStore.fetchBoardList({ ...etcStore.noticeList.payload }, refresh)
  } catch {
    isError.value = true
  } finally {
    isLoading.value = false
  }
}
</script>

<template>
  <NuxtLayout name="board">
    <template #leftSideBar>
      <NovaBoxBoardSideMenu :active="'notice'" />
    </template>

    <div class="board-wrapper">
      <div class="list-area">
        <span class="title">{{ $t('board.title.notice') }}</span>
        <div class="list-box">
          <NovaBoxEmptyMessage
            v-if="
              etcStore.noticeList.refresh &&
              !etcStore.noticeList.items.length &&
              !isError
            "
            :message="$t('statusMessages.boardList.empty')"
            :fill="true"
          />

          <NovaBoxEmptyMessage
            v-if="!etcStore.noticeList.items.length && isError"
            :message="$t('statusMessages.boardList.error')"
            :fill="true"
          />

          <NovaLoadingIndicator
            v-if="etcStore.noticeList.refresh && isLoading"
            :size="80"
            :fill="true"
            :bg-bright="'light'"
          />

          <NovaRecyclerGrid
            v-if="etcStore.noticeList.items.length"
            :items="etcStore.noticeList.items"
            :is-last="etcStore.noticeList.last"
            :is-loading="isLoading"
            :is-error="isError"
            :is-refresh="etcStore.noticeList.refresh"
            :key-name="'nttGroupId'"
            :page-mode="true"
            :responsive="{
              mobile: {
                columns: 1,
                rowGap: 12,
                columnGap: 12,
              },
              tablet: {
                columns: 1,
                rowGap: 12,
                columnGap: 12,
              },
              desktop: {
                columns: 1,
                rowGap: 12,
                columnGap: 12,
              },
            }"
            @load-more="fetchNotices"
          >
            <template #item="{ item }">
              <NovaBoxBoardListNoticeItem
                :source="item"
                :notice-type="'notice'"
              />
            </template>
          </NovaRecyclerGrid>
        </div>
      </div>
    </div>
  </NuxtLayout>
</template>

<style lang="scss" scoped>
.board-wrapper,
.list-area,
.list-box,
.list {
  height: 100%;
  overflow: hidden;
}

.board-wrapper {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 40px;
  padding-top: 20px;
  color: $color-text-2;

  .list-area {
    display: flex;
    flex-direction: column;
    flex-grow: 1;

    > .title {
      @include text-style($text-heading2-bold);
      line-height: 40px;
    }

    .list-box {
      padding-top: 30px;
    }
  }
}

@include mobile {
  .board-wrapper {
    padding: 15px 20px;
    flex-direction: column;
    gap: 20px;

    .list-area {
      width: 100%;

      .list-box {
        padding-top: 15px;
      }
    }
  }
}
</style>
